import { ReactNode } from "react";
import { Stack, Text, XStack, YStack } from "tamagui";
import KioskIcon, { KioskIconName } from "../KioskIcon";
import { kiosk } from "../kioskTheme";
import { v2 } from "@screen/Home/pages/GasShop/skin";

/**
 * 藍色漸層 header。
 * - 首頁（無 onBack）：品牌圖標徽章 + 標題 + 副標，較高、底部圓角、柔光層，較有設計感。
 * - 子頁（有 onBack）：返回鍵 + 標題，精簡。
 * 柔光用低透明度漸層斜光（非裝飾性圓圈），保留深度但不喧賓奪主。
 */
export const KioskHeader = ({
  title,
  subtitle,
  onBack,
  right,
}: {
  title: string;
  /** 字串照原樣套副標樣式；傳 node 時由呼叫端自訂（如賣方身分＋可撥打電話）。 */
  subtitle?: ReactNode;
  onBack?: () => void;
  right?: ReactNode;
}) => {
  const isHome = !onBack;
  return (
    /* 首頁的頁首原本是一條藍色漸層帶（kiosk-grad + 柔光層 + 投影 + 圓角）。
       頁面底色換成淡霧之後，那條帶子會重演「實藍帶壓在淡底上」的老問題——
       它是整頁唯一的重色，卻不承載任何操作。改成無底色、深藍字，
       和子頁的 bar 統一，也和瓦斯訂購那邊的釘住標題列同一套語彙。 */
    <YStack
      position="relative"
      /* 不能有 overflow: hidden——賣方身分的展開小卡是絕對定位在名稱下方的，
         會被裁掉。原本需要它是為了裁切藍色帶的柔光層與圓角，帶子拿掉後就不需要了。 */
      px={18}
      pt={isHome ? 18 : 14}
      /* 首頁原本 pb 22 是為了撐起那條藍色漸層帶——帶子拿掉後那段內距只是空氣，
         和捲動區自己的上內距疊起來，標題與第一張卡之間會空掉一大截。 */
      pb={isHome ? 6 : 12}
      style={{ flex: "0 0 auto" }}>

      {isHome ? (
        /* 副標一律貼在標題下的左欄，不再讓「賣方身分」獨佔一整列。
           當初移出去是因為擠在左欄會被右側的身分選單壓到換行——但 SupplierIdentityBar
           本身就會截斷（numberOfLines=1 + flexShrink），真正的問題是那一列沒有給它
           minWidth={0}，於是它撐開了整列而不是縮短自己。補上之後就塞得回去，
           省下一整列（含 gap 約 28px）。
           alignSelf 靠左：它內部是 justify-content: flex-end（版頭那邊靠右對齊用的），
           不收窄的話在左欄裡會被推到最右邊，看起來像沒對齊標題。 */
        <XStack position="relative" ai="center" gap={12}>
          <YStack flex={1} minWidth={0}>
            <Text color={v2.hazeInk} fontSize={20} fontWeight="900" letterSpacing={-0.4}>
              {title}
            </Text>
            {typeof subtitle === "string" ? (
              <Text color={v2.hazeSub} fontSize={12} mt={2} fontWeight="500">
                {subtitle}
              </Text>
            ) : subtitle ? (
              <YStack alignSelf="flex-start" maxWidth="100%" mt={2}>
                {subtitle}
              </YStack>
            ) : null}
          </YStack>
          {right}
        </XStack>
      ) : (
        // 子頁：無底色、深色文字；返回鍵置左、標題置中、右側等寬留白讓標題真正置中
        <XStack ai="center" position="relative">
          <Stack width={38} ai="flex-start">
            <Stack
              onPress={onBack}
              cursor="pointer"
              width={34}
              height={34}
              borderRadius={11}
              ai="center"
              jc="center"
              backgroundColor={kiosk.pillBg}
              pressStyle={{ scale: 0.9, opacity: 0.8 }}>
              <Stack style={{ transform: "scaleX(-1)" }}>
                <KioskIcon name="chevron" size={20} color={kiosk.blue} strokeWidth={2.6} />
              </Stack>
            </Stack>
          </Stack>
          <Text flex={1} textAlign="center" color={kiosk.ink} fontSize={17} fontWeight="800" letterSpacing={-0.3}>
            {title}
          </Text>
          <Stack width={38} ai="flex-end">
            {right}
          </Stack>
        </XStack>
      )}
    </YStack>
  );
};

/** 進度點（首次 vs 熟客步數不同）。 */
export const StepDots = ({ total, current }: { total: number; current: number }) => (
  <XStack gap={6} flex={1} jc="center">
    {Array.from({ length: total }).map((_, i) => (
      <Stack
        key={i}
        width={26}
        height={5}
        borderRadius={3}
        backgroundColor={i < current ? kiosk.green : i === current ? kiosk.blue : "#e2e6ee"}
        style={{ transition: "background 0.3s" }}
      />
    ))}
  </XStack>
);

/** 全螢幕步驟 sheet 外殼：✕、進度點、右上標籤、內容、底部按鈕列。 */
export const SheetShell = ({
  onClose,
  dotsTotal,
  dotsCurrent,
  rightLabel,
  children,
  footer,
}: {
  onClose: () => void;
  dotsTotal: number;
  dotsCurrent: number;
  rightLabel?: string;
  children: ReactNode;
  footer: ReactNode;
}) => (
  <YStack
    className="kiosk-sheetin"
    position="absolute"
    t={0}
    l={0}
    r={0}
    b={0}
    backgroundColor="#fff"
    style={{ zIndex: 60 }}>
    <XStack px={18} py={14} ai="center" gap={12} style={{ flex: "0 0 auto" }}>
      <Stack onPress={onClose} cursor="pointer" width={26} pressStyle={{ opacity: 0.5 }}>
        <KioskIcon name="close" size={22} color={kiosk.grey} />
      </Stack>
      <StepDots total={dotsTotal} current={dotsCurrent} />
      <Text fontSize={12} color={kiosk.grey} fontWeight="600" minWidth={26} textAlign="right">
        {rightLabel || ""}
      </Text>
    </XStack>
    <YStack flex={1} px={22} pb={16} className="kiosk-noscroll" style={{ overflowY: "auto" }}>
      {children}
    </YStack>
    <YStack px={22} pt={14} pb={26} style={{ flex: "0 0 auto" }}>
      {footer}
    </YStack>
  </YStack>
);

/** 大問句 + 副標。 */
export const Question = ({ q, sub }: { q: string; sub?: string }) => (
  <YStack mt={8} mb={20}>
    <Text fontSize={26} fontWeight="800" letterSpacing={-0.6} color={kiosk.ink}>
      {q}
    </Text>
    {sub && (
      <Text fontSize={14} color={kiosk.grey} mt={4}>
        {sub}
      </Text>
    )}
  </YStack>
);

/** 點餐機式大按鈕（icon + 文字 + 選取打勾）。 */
export const BigOption = ({
  icon,
  label,
  selected,
  onPress,
  sub,
}: {
  icon?: KioskIconName;
  label: string;
  selected?: boolean;
  onPress: () => void;
  sub?: ReactNode;
}) => (
  <YStack
    onPress={onPress}
    cursor="pointer"
    position="relative"
    ai="center"
    jc="center"
    py={20}
    px={10}
    borderRadius={18}
    borderWidth={2}
    borderColor={selected ? kiosk.blue : kiosk.line}
    backgroundColor={selected ? kiosk.pillBg : "#fff"}
    className={selected ? "kiosk-pop" : undefined}
    pressStyle={{ scale: 0.95 }}
    style={{ transition: "border-color 0.15s, background 0.15s" }}>
    {icon && (
      <Stack mb={9}>
        <KioskIcon name={icon} size={36} color={selected ? kiosk.blue : "#46506a"} />
      </Stack>
    )}
    <Text fontSize={16} fontWeight="700" color={selected ? kiosk.blue : "#33405c"}>
      {label}
    </Text>
    {sub}
    {selected && (
      <Stack
        position="absolute"
        t={-9}
        r={-9}
        width={26}
        height={26}
        borderRadius={13}
        backgroundColor={kiosk.blue}
        ai="center"
        jc="center"
        style={{ boxShadow: "0 2px 5px -1px rgba(29,111,255,.3)" }}>
        <KioskIcon name="check" size={15} color="#fff" strokeWidth={2.6} />
      </Stack>
    )}
  </YStack>
);

/** 底部主 CTA 漸層按鈕。 */
export const KioskCTA = ({
  label,
  onPress,
  disabled,
  right,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  right?: ReactNode;
}) => (
  /* 扁平：單一實色、無漸層、無光澤。膠囊圓角保留——扁平講的是表面不是形狀，
     而在淡霧底上，圓角愈大愈能靠「形狀」而非「陰影」把按鈕從背景裡分出來。
     只留一道極淺的接觸影把它壓在紙上，回饋交給 gas-press 的縮放。
     Kiosk 三顆主按鈕（這顆、浮動購物車列、一鍵重新訂購）現在同一套。 */
  <XStack
    className={disabled ? undefined : "gas-press"}
    backgroundColor={disabled ? "#cdd3dd" : kiosk.blue}
    borderRadius={999}
    px={20}
    py={17}
    ai="center"
    jc={right ? "space-between" : "center"}
    cursor={disabled ? "not-allowed" : "pointer"}
    onPress={disabled ? undefined : onPress}
    pressStyle={disabled ? undefined : { opacity: 0.88 }}
    style={{ boxShadow: disabled ? undefined : "0 2px 8px -4px rgba(29,111,255,.45)" }}>
    <Text color="#fff" fontSize={17} fontWeight="800">
      {label}
    </Text>
    {right}
  </XStack>
);

/** 次要（返回）按鈕。 */
export const KioskBackBtn = ({ label, onPress }: { label: string; onPress: () => void }) => (
  <XStack
    backgroundColor="#eef1f6"
    borderRadius={16}
    px={20}
    py={17}
    ai="center"
    jc="center"
    cursor="pointer"
    onPress={onPress}
    pressStyle={{ opacity: 0.8 }}>
    <Text color="#39435c" fontSize={17} fontWeight="800">
      {label}
    </Text>
  </XStack>
);
